import { createElement as rc } from 'react';
import log from '@sstdev/lib_logging';
import { css } from 'styled-components';
import PropTypes from 'prop-types';
import buttonStyling from '../styling/themes/gl2025/buttons';
import Icon from './Icon';
import styled from '../styled';
import { styleObjectToCss } from '../styling/utilities';
import View from './View';
import Badge from './Badge';

const StyledButton = styled('button').attrs(props => {
    const { variant: _variant, destructive, size, theme, text, icon, buttonStyle } = props; // todo gl2025 remove buttonStyle
    const variant = _variant === 'inLine' ? 'tertiary' : _variant;
    let style;
    try {
        style = {
            ...buttonStyling[theme.darkMode ? 'dark' : 'light'][destructive ? 'destructive' : 'nondestructive'][
                variant
            ][size],
            height: 'fit-content',
            width: 'fit-content'
        };
    } catch (error) {
        error.message = `Button styling not found for variant/destructive/size combination: ${destructive ? 'destructive' : 'nondestructive'}, ${variant}, ${size}: ${error.message}`;
        error.stack = `Button styling not found for variant/destructive/size combination: ${destructive ? 'destructive' : 'nondestructive'}, ${variant}, ${size}\nOriginal stack:\n${error.stack}`;
        log.error(error);
        throw error;
    }
    // todo gl2025: remove check for IconAction/BigIconAction/round
    const iconOnly = !text || !text.length || ['IconAction', 'BigIconAction', 'round'].includes(buttonStyle);
    const iconIsDot = icon === 'dot';
    return { style, iconOnly, iconIsDot, type: props.type || 'button' };
})`
    position: relative;
    ${({ style, iconIsDot, iconOnly }) => css`
        ${styleObjectToCss({
            ...(iconOnly ? style.default.iconOnly : style.default.button),
            ...(iconIsDot ? style.default.dot : {})
        })}
    `}

    &:not(:disabled) {
        &:hover {
            ${({ style, iconIsDot, iconOnly }) => css`
                ${styleObjectToCss({
                    ...(iconOnly ? style.hover.iconOnly : style.hover.button),
                    ...(iconIsDot ? style.hover.dot : {})
                })}
            `}
            cursor: pointer;
        }

        &:focus,
        &:focus-visible,
        &:active {
            outline-width: 0;
        }

        &:focus-visible,
        &:active {
            ${({ style, iconIsDot, iconOnly }) => css`
                ${styleObjectToCss({
                    ...(iconOnly ? style.focused.iconOnly : style.focused.button),
                    ...(iconIsDot ? style.focused.dot : {})
                })}
            `}
        }
    }

    &:disabled {
        ${({ style, iconIsDot, iconOnly }) => css`
            ${styleObjectToCss({
                ...(iconOnly ? style.disabled.iconOnly : style.disabled.button),
                ...(iconIsDot ? style.disabled.dot : {})
            })}
        `}
    }
`;

// color is handled by button styling
// nothing else changes between button states for text, so default for every state is sufficient
const StyledText = styled('span').attrs(props => {
    const { variant, destructive, size, theme } = props;
    let style;
    try {
        style =
            buttonStyling[theme.darkMode ? 'dark' : 'light'][destructive ? 'destructive' : 'nondestructive'][variant][
                size
            ];
    } catch (error) {
        error.message = `Button styling not found for variant/destructive/size combination: ${destructive ? 'destructive' : 'nondestructive'}, ${variant}, ${size}: ${error.message}`;
        error.stack = `Button styling not found for variant/destructive/size combination: ${destructive ? 'destructive' : 'nondestructive'}, ${variant}, ${size}\nOriginal stack:\n${error.stack}`;
        log.error(error);
        throw error;
    }
    return { style };
})`
    ${({ style }) => css`
        ${styleObjectToCss(style.default.text)}
    `}
`;

const BadgeWrapper = styled(View).attrs({ name: 'badge-wrapper' })`
    position: absolute;
    top: 0;
    right: 0;
`;
BadgeWrapper.displayName = 'BadgeWrapper';

export default function Button(props) {
    const {
        text,
        icon,
        size = 'sm',
        iconTrailing = false,
        // eslint-disable-next-line no-unused-vars
        color, // don't pass this to Icon component; Icons within buttons should receive color styling from parent,
        buttonStyle, // todo gl2025 remove
        badge,
        hint,
        // eslint-disable-next-line no-unused-vars
        'data-testid': _excludeFromOtherProps, // strip from ...otherProps so Icon children don't duplicate the Button's data-testid
        ...otherProps
    } = props;

    const useIcon = icon && icon.length;
    // todo gl2025: remove check for IconAction/BigIconAction/round
    const useText = text && text.length && !['IconAction', 'BigIconAction', 'round'].includes(buttonStyle);

    // special case: to use a dot instead of an icon, set icon to 'dot'
    // defaults (everything except dot)
    let iconName = icon;
    let iconFill = false;
    let iconSize = useText ? 'md' : size; // if text present, size is medium; if icon-only, use button size
    // handle if icon is dot
    if (icon === 'dot') {
        iconName = 'circle';
        iconFill = true;
        iconSize = 'xs';
    }

    // todo gl2025: `children` prop for Icons is only needed for backwards compatibility,
    //  it can be removed when old theme no longer supported

    let ariaLabel = props.title || text || hint || props.alt || icon;
    // Support using '+' as the visible title for add buttons, while providing a more descriptive aria-label for screen readers
    if (ariaLabel === '+') {
        ariaLabel = 'Add';
    }
    // prettier-ignore
    return (
        // color: 'currentColor' is css to use parent's color css
        rc(StyledButton, { ...props, 'aria-label': ariaLabel, role: 'button' },
            useIcon && !iconTrailing && rc(Icon, { ...otherProps, name: iconName, size: iconSize, fill: iconFill, color: 'currentColor', children: iconName }),
            useText && rc(StyledText, { variant: props.variant, destructive: props.destructive, size }, text),
            useIcon && iconTrailing && rc(Icon, { ...otherProps, name: iconName, size: iconSize, fill: iconFill, color: 'currentColor', children: iconName }),
            badge && rc(BadgeWrapper, null, rc(Badge, { badge }))
        )
    );
}

Button.propTypes = {
    text: PropTypes.string, // text to display in button; for icon-only, leave empty
    icon: PropTypes.string, // name of icon to display in button, or 'dot' for special case; for text-only, leave empty
    size: PropTypes.oneOf(['xs', 'sm', 'lg']).isRequired,
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'link']).isRequired,
    badge: PropTypes.oneOf(['error', 'warning', 'success']),
    destructive: PropTypes.bool,
    disabled: PropTypes.bool
};

Button.defaultProps = {
    destructive: false,
    disabled: false,
    badge: undefined
};
